import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { Skeleton } from '../ui/skeleton'
import { 
  Sparkles,
  Lightbulb,
  TrendingUp,
  Zap,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  ChevronRight,
  Brain
} from 'lucide-react'
import { mockAI } from '../../utils/mockAI'

interface AISuggestionsProps {
  type: 'colors' | 'fonts' | 'messaging' | 'positioning' | 'strategy'
  brandData?: any
  currentData?: any
  onApplySuggestion?: (suggestion: any) => void
  className?: string
  compact?: boolean
}

export function AISuggestions({ 
  type, 
  brandData, 
  currentData, 
  onApplySuggestion,
  className = '',
  compact = false
}: AISuggestionsProps) {
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const loadSuggestions = async () => {
    setIsLoading(true)
    try {
      const result = await mockAI.getSuggestions({
        type,
        brandData,
        currentData
      })
      setSuggestions(result)
      setHasLoaded(true)
    } catch (error) {
      console.error('Failed to load suggestions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!hasLoaded) {
      loadSuggestions()
    }
  }, [type, hasLoaded])

  const handleApplySuggestion = (suggestion: any) => {
    onApplySuggestion?.(suggestion)
    // In a real app, you might want to track this interaction
  }

  const getTypeIcon = () => {
    switch (type) {
      case 'colors': return '🎨'
      case 'fonts': return '📝'
      case 'messaging': return '💬'
      case 'positioning': return '🎯'
      case 'strategy': return '📈'
      default: return '💡'
    }
  }

  const getTypeTitle = () => {
    switch (type) {
      case 'colors': return 'Color Suggestions'
      case 'fonts': return 'Typography Suggestions'
      case 'messaging': return 'Messaging Ideas'
      case 'positioning': return 'Positioning Suggestions'
      case 'strategy': return 'Strategy Recommendations'
      default: return 'AI Suggestions'
    }
  }

  if (compact) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium">AI Suggestions</span>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={loadSuggestions}
            disabled={isLoading}
          >
            {isLoading ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {suggestions.slice(0, 2).map((suggestion, index) => (
              <div key={index} className="p-3 border rounded-lg bg-blue-50/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{suggestion.title}</span>
                  <Badge variant="outline" className="text-xs">
                    {Math.round(suggestion.confidence * 100)}%
                  </Badge>
                </div>
                <p className="text-xs text-slate-600 mb-2">{suggestion.description}</p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-6 text-xs"
                  onClick={() => handleApplySuggestion(suggestion)}
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Try This
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <span className="text-lg">{getTypeIcon()}</span>
            {getTypeTitle()}
          </CardTitle>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={loadSuggestions}
            disabled={isLoading}
          >
            {isLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
        <CardDescription>
          AI-powered suggestions based on your brand context and industry best practices
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))}
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No suggestions available at the moment</p>
            <Button 
              size="sm" 
              variant="outline" 
              className="mt-2"
              onClick={loadSuggestions}
            >
              Refresh Suggestions
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <div key={index}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{suggestion.title}</h4>
                      <Badge variant="outline" className="text-xs">
                        {Math.round(suggestion.confidence * 100)}% match
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">
                      {suggestion.description}
                    </p>
                    
                    {/* Render suggestion value based on type */}
                    {suggestion.value && (
                      <div className="mb-3">
                        {type === 'colors' && Array.isArray(suggestion.value) && (
                          <div className="flex gap-2 mb-2">
                            {suggestion.value.map((color: string, colorIndex: number) => (
                              <div key={colorIndex} className="flex flex-col items-center">
                                <div 
                                  className="w-8 h-8 rounded border"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="text-xs text-slate-500 mt-1">{color}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {type === 'fonts' && typeof suggestion.value === 'object' && (
                          <div className="p-3 bg-slate-50 rounded-lg mb-2">
                            <div className="font-medium" style={{ fontFamily: suggestion.value.family }}>
                              {suggestion.value.family}
                            </div>
                            <div className="text-sm text-slate-600">
                              Weights: {suggestion.value.weights?.join(', ')}
                            </div>
                          </div>
                        )}
                        
                        {(type === 'messaging' || type === 'positioning' || type === 'strategy') && typeof suggestion.value === 'string' && (
                          <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-l-4 border-blue-500 mb-2">
                            <p className="text-sm font-medium text-slate-800">
                              "{suggestion.value}"
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleApplySuggestion(suggestion)}
                        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        Apply This
                      </Button>
                      <Button size="sm" variant="outline">
                        <ThumbsUp className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline">
                        <ThumbsDown className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                {index < suggestions.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Pro Tip</span>
          </div>
          <p className="text-sm text-blue-800">
            These suggestions are generated based on your brand context, industry trends, and design best practices. 
            You can always customize them to better fit your vision.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}